// Sidebar status icon — Lucide iconography, Cursor-parity.
//
//   running                                  → Loader (animate-spin)
//   waiting / finished / interrupted / stuck → CircleCheck (idle)
//   error                                    → CircleAlert (red)
//   archived (section, passed in by parent)  → Archive
//
// Colour binding: only `error` sets its own colour (status-error red).
// Every other icon inherits `currentColor` from the parent (the agent
// list row) so the icon and the agent name stay locked in lockstep —
// when the row is selected and the name brightens, the icon brightens
// with it. Sprint `sidebar-row-alignment-and-color` (2026-05-12)
// introduced the inherit pattern; previously each branch hardcoded
// `text-muted-foreground` which broke parity with the name on hover /
// selected states.
//
// strokeWidth bumped to 2.25 (default Lucide is 2). The slightly heavier
// stroke compensates for the darker row text colour (#7C7C7C) so the
// icon stays legible against the dark sidebar background.

import type { AgentStatus } from '../types';
import { Icon } from './atoms/Icon';
import { useTranslation } from '../i18n';

const STROKE_WIDTH = 2.25;

export interface StatusIconProps {
  status: AgentStatus;
  /** When true, overrides `status` and renders the Archive glyph. The
   * parent decides because "archived" is a section, not a status. */
  archived?: boolean;
  /** Icon size in px. Defaults to 13 (matches existing 13 px sidebar
   * iconography — Pin button, hover Archive, hover Trash2). */
  size?: number;
  className?: string;
}

export function StatusIcon({
  status,
  archived = false,
  size = 13,
  className = '',
}: StatusIconProps) {
  const { t } = useTranslation();

  if (archived) {
    return (
      <Icon
        name="Archive"
        size={size}
        strokeWidth={STROKE_WIDTH}
        className={className}
        aria-label={t('status.archived')}
        data-testid="status-icon-archived"
        data-status="archived"
      />
    );
  }

  if (status === 'running') {
    return (
      <Icon
        name="Loader"
        size={size}
        strokeWidth={STROKE_WIDTH}
        className={`animate-spin ${className}`}
        aria-label={t('status.running')}
        data-testid="status-icon-running"
        data-status="running"
      />
    );
  }

  if (status === 'error') {
    return (
      <Icon
        name="CircleAlert"
        size={size}
        strokeWidth={STROKE_WIDTH}
        className={`text-status-error ${className}`}
        aria-label={t('status.error')}
        data-testid="status-icon-error"
        data-status="error"
      />
    );
  }

  // waiting / finished / interrupted / stuck — all collapse to "idle"
  // (D-008). One signal: the agent is not working; send a message to
  // continue.
  return (
    <Icon
      name="CircleCheck"
      size={size}
      strokeWidth={STROKE_WIDTH}
      className={className}
      aria-label={t('status.idle')}
      data-testid={`status-icon-${status}`}
      data-status={status}
    />
  );
}
